import React, { useEffect, useRef, useState } from "react";
import { Plus, Trash2, ArrowUp, ArrowDown, Save, Globe, Copy, Code, ExternalLink, Sparkles, GripVertical, Bold, Italic, Underline, Link2, Check } from "lucide-react";
import { useData } from "@/context/DataContext";
import { PageHeader, EventSelector, NoEvent } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import LandingView from "@/components/LandingView";
import { uploadFile } from "@/lib/upload";
import client from "@/lib/api";
import { toast } from "sonner";

const BLOCK_META = { ai_page: "✨ Page IA (design complet)", hero: "Hero", text: "Texte", image: "Photo", gallery: "Galerie", button: "Bouton", video: "Vidéo", youtube: "YouTube", maps: "Google Maps", programme: "Programme", speakers: "Intervenants", exposants: "Exposants", sponsors: "Sponsors", countdown: "Compte à rebours", registration: "Inscription", faq: "FAQ", divider: "Séparateur", footer: "Pied de page" };
const PALETTE = ["hero", "text", "image", "gallery", "button", "video", "youtube", "maps", "programme", "speakers", "exposants", "sponsors", "countdown", "registration", "faq", "divider", "footer"];
const HAS_ITEMS = { speakers: ["name", "role", "bio", "linkedin", "website", "photo"], exposants: ["name", "stand", "description", "website", "logo"], sponsors: ["name", "logo", "website"], faq: ["q", "a"], gallery: ["image", "link"] };
const PLACE = { name: "Nom", role: "Rôle", bio: "Bio / présentation", linkedin: "LinkedIn (URL)", website: "Site web (URL)", q: "Question", a: "Réponse", link: "Lien (URL)", stand: "N° de stand", description: "Description" };
const UPLOAD_KEYS = ["photo", "logo", "image"];
const newBlock = (type) => ({ id: `b${Date.now()}${Math.random().toString(36).slice(2, 5)}`, type, title: "", text: "", html: "", bg: "#ffffff", accent: "", image: "", url: "", label: "", query: "", align: "center", width: 100, color: "", items: [] });
const DEFAULT_BLOCKS = () => [newBlock("hero"), newBlock("programme"), newBlock("registration"), newBlock("footer")];
const AI_GENERATION_TIMEOUT_SECONDS = 245;

function RichTextEditor({ value, onChange }) {
  const ref = useRef(null);
  useEffect(() => { if (ref.current && ref.current.innerHTML !== (value || "")) ref.current.innerHTML = value || ""; /* eslint-disable-next-line */ }, []);
  const cmd = (c, v) => { document.execCommand(c, false, v); ref.current?.focus(); onChange(ref.current.innerHTML); };
  const addLink = () => { const url = prompt("URL du lien"); if (url) cmd("createLink", url); };
  const Btn = ({ onDo, children, ...p }) => <button type="button" onMouseDown={(e) => { e.preventDefault(); onDo(); }} className="px-2 py-1 rounded hover:bg-slate-200 text-sm" {...p}>{children}</button>;
  return (
    <div className="border border-border rounded-lg">
      <div className="flex flex-wrap items-center gap-1 p-1.5 border-b border-border bg-slate-50">
        <Btn onDo={() => cmd("bold")}><Bold className="h-4 w-4" /></Btn>
        <Btn onDo={() => cmd("italic")}><Italic className="h-4 w-4" /></Btn>
        <Btn onDo={() => cmd("underline")}><Underline className="h-4 w-4" /></Btn>
        <select onChange={(e) => cmd("fontSize", e.target.value)} defaultValue="3" className="text-xs rounded border border-border px-1 py-0.5" data-testid="rte-size"><option value="2">Petit</option><option value="3">Normal</option><option value="5">Grand</option><option value="7">Titre</option></select>
        <select onChange={(e) => cmd("fontName", e.target.value)} defaultValue="" className="text-xs rounded border border-border px-1 py-0.5" data-testid="rte-font"><option value="">Police</option><option value="Arial">Arial</option><option value="Georgia">Georgia</option><option value="Courier New">Mono</option><option value="Poppins">Poppins</option></select>
        <input type="color" onChange={(e) => cmd("foreColor", e.target.value)} className="h-7 w-8 rounded border border-border" title="Couleur du texte" data-testid="rte-color" />
        <Btn onDo={addLink} data-testid="rte-link"><Link2 className="h-4 w-4" /></Btn>
      </div>
      <div ref={ref} contentEditable suppressContentEditableWarning onInput={(e) => onChange(e.currentTarget.innerHTML)} data-testid="rich-text-editor" className="p-3 min-h-[110px] text-sm focus:outline-none [&_a]:text-indigo-600 [&_a]:underline" />
    </div>
  );
}

const AI_BLOCKS = [
  { key: "hero", label: "Hero (bannière)" }, { key: "text", label: "Texte" },
  { key: "programme", label: "Programme" }, { key: "speakers", label: "Intervenants" },
  { key: "exposants", label: "Exposants" }, { key: "sponsors", label: "Sponsors" },
  { key: "countdown", label: "Compte à rebours" }, { key: "registration", label: "Inscription" },
  { key: "faq", label: "FAQ" }, { key: "footer", label: "Pied de page" },
];
const AI_STYLES = ["Moderne", "Minimaliste", "Coloré", "Corporate / Business", "Élégant / Luxe", "Festif"];

export default function LandingEditor() {
  const { selectedEvent, company, daysByEvent, activitiesByDay } = useData();
  const [blocks, setBlocks] = useState([]);
  const [slug, setSlug] = useState("");
  const [published, setPublished] = useState(false);
  const [reg, setReg] = useState(null);
  const [loaded, setLoaded] = useState(false);
  const [aiQuota, setAiQuota] = useState({ used: 0, free: 3, remaining: 3, extra_price_eur: 50 });
  const [aiInstruction, setAiInstruction] = useState("");
  const [aiLoading, setAiLoading] = useState(false);
  const [aiVariants, setAiVariants] = useState(null); // {variants:[{blocks,label}], activeIdx}
  const [preAIBlocks, setPreAIBlocks] = useState(null); // saved blocks before applying an AI variant
  const [reviewMode, setReviewMode] = useState(false); // true after applying variant, before publish
  const [refineInstruction, setRefineInstruction] = useState("");
  const [refining, setRefining] = useState(false);
  const [aiJobId, setAiJobId] = useState(null);
  const [aiJobPhase, setAiJobPhase] = useState("");
  const [refineJobId, setRefineJobId] = useState(null);
  const [refineJobPhase, setRefineJobPhase] = useState("");
  const aiPollRef = useRef(null);
  const refinePollRef = useRef(null);
  const dragI = useRef(null);

  useEffect(() => {
    if (!selectedEvent) return;
    setLoaded(false);
    Promise.all([client.get(`/landing/${selectedEvent.id}`), client.get(`/registration/${selectedEvent.id}`), client.get(`/ai/landing/quota`).catch(() => ({ data: null }))]).then(([l, r, q]) => {
      const data = l.data;
      if (data && data.blocks?.length) { setBlocks(data.blocks); setSlug(data.slug || ""); setPublished(!!data.published); setPreAIBlocks(data.preAIBlocks || null); }
      else { setBlocks(DEFAULT_BLOCKS()); setSlug(""); setPublished(false); setPreAIBlocks(null); }
      setReg(r.data && Object.keys(r.data).length ? r.data : null);
      if (q.data) setAiQuota(q.data);
      setLoaded(true);
    });
  }, [selectedEvent]);

  const generateAI = async () => {
    if (!aiInstruction.trim()) { toast.error("Écrivez une instruction ou tapez « libre arbitre »"); return; }
    setAiLoading(true);
    setAiJobPhase("🚀 Envoi de votre brief à l'IA…");
    try {
      const { data } = await client.post(`/ai/landing/generate`, {
        eventId: selectedEvent.id, instruction: aiInstruction, currentBlocks: blocks,
      });
      if (!data.jobId) { throw new Error("no job id"); }
      setAiJobId(data.jobId);
      // Start polling
      clearInterval(aiPollRef.current);
      let elapsed = 0;
      aiPollRef.current = setInterval(async () => {
        elapsed += 3;
        if (elapsed >= AI_GENERATION_TIMEOUT_SECONDS) {
          clearInterval(aiPollRef.current);
          aiPollRef.current = null;
          try { await client.post(`/ai/landing/job/${data.jobId}/cancel`); } catch {}
          toast.error("La génération dépasse 4 minutes. Elle a été arrêtée : relancez votre demande.");
          setAiLoading(false); setAiJobId(null); setAiJobPhase("");
          return;
        }
        try {
          const { data: j } = await client.get(`/ai/landing/job/${data.jobId}`);
          if (j.phase) setAiJobPhase(j.phase);
          if (elapsed < 10) setAiJobPhase("🎨 L'IA compose votre landing…");
          else if (elapsed < 30) setAiJobPhase("Mise en page en cours — contenu conservé…");
          else if (elapsed < 55) setAiJobPhase("✨ Finalisation, patientez…");
          else setAiJobPhase(`⏳ En cours (${elapsed}s)…`);
          if (j.status === "done") {
            clearInterval(aiPollRef.current);
            aiPollRef.current = null;
            const variants = j.variants || [];
            if (!variants.length) { toast.error("L'IA n'a rien retourné — reformulez"); setAiLoading(false); setAiJobId(null); return; }
            setAiVariants({ variants, activeIdx: 0, usage: j.usage });
            setAiQuota((q) => ({ ...q, used: j.quota_used, remaining: j.remaining }));
            const u = j.usage || {};
            toast.success(`${variants.length} ${variants.length === 1 ? "design généré" : "designs générés"} · tous les blocs conservés · coût estimé $${(u.usd || 0).toFixed(4)}`);
            setAiLoading(false); setAiJobId(null); setAiJobPhase("");
          } else if (j.status === "error" || j.status === "cancelled") {
            clearInterval(aiPollRef.current);
            aiPollRef.current = null;
            toast.error(j.error || (j.status === "cancelled" ? "Génération annulée" : "Échec IA — réessayez"));
            setAiLoading(false); setAiJobId(null); setAiJobPhase("");
          }
        } catch (err) {
          const status = err?.response?.status;
          if (status === 404) {
            clearInterval(aiPollRef.current);
            aiPollRef.current = null;
            toast.error("Le job IA n'est plus disponible. Relancez votre demande.");
            setAiLoading(false); setAiJobId(null); setAiJobPhase("");
          } else {
            setAiJobPhase("🌐 Connexion momentanément interrompue — nouvelle tentative…");
          }
        }
      }, 3000);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Impossible de démarrer la génération");
      setAiLoading(false); setAiJobPhase("");
    }
  };

  const cancelAI = async () => {
    if (aiJobId) {
      try { await client.post(`/ai/landing/job/${aiJobId}/cancel`); } catch {}
    }
    clearInterval(aiPollRef.current); aiPollRef.current = null;
    setAiLoading(false); setAiJobId(null); setAiJobPhase("");
    toast.info("Génération annulée");
  };

  const applyVariant = (idx) => {
    const v = aiVariants?.variants?.[idx];
    if (!v?.blocks?.length) return;
    // La source validée contient aussi les blocs ajoutés depuis la dernière génération.
    const originalToKeep = v.blocks.flatMap((b) => b.sourceBlocks || [b]);
    setPreAIBlocks(originalToKeep);
    setBlocks(v.blocks);
    setAiVariants(null);
    setAiInstruction("");
    setReviewMode(true);
    // Persist immediately so preAIBlocks survives un refresh de la page.
    client.put(`/landing/${selectedEvent.id}`, {
      blocks: v.blocks, slug, published, preAIBlocks: originalToKeep || null,
    }).catch(() => {});
    toast.success(`✅ Variante « ${v.label} » appliquée — passez en revue puis publiez`);
  };

  const restorePreAI = () => {
    if (!preAIBlocks?.length) { toast.error("Aucune version antérieure à restaurer"); return; }
    const restored = preAIBlocks;
    setBlocks(restored);
    setPreAIBlocks(null);
    setReviewMode(false);
    // Persist : on efface aussi preAIBlocks côté serveur car l'utilisateur repart des originaux.
    client.put(`/landing/${selectedEvent.id}`, {
      blocks: restored, slug, published: false, preAIBlocks: null,
    }).then(() => setPublished(false)).catch(() => {});
    toast.success("↩︎ Retour aux blocs originaux — tout le contenu (photos, textes) est conservé");
  };

  const applyRefinement = async () => {
    if (!refineInstruction.trim()) { toast.error("Écrivez d'abord une modification à demander"); return; }
    const aiBlock = blocks.find((b) => b.type === "ai_page");
    if (!aiBlock) { toast.error("Aucune page IA à modifier"); return; }
    setRefining(true);
    setRefineJobPhase("🚀 Envoi de votre demande à l'IA…");
    try {
      const { data } = await client.post(`/ai/landing/refine`, {
        eventId: selectedEvent.id, currentBlocks: blocks,
        currentDesign: aiBlock.design || {}, instruction: refineInstruction,
      });
      if (!data.jobId) { throw new Error("no job id"); }
      setRefineJobId(data.jobId);
      clearInterval(refinePollRef.current);
      let elapsed = 0;
      refinePollRef.current = setInterval(async () => {
        elapsed += 3;
        if (elapsed >= AI_GENERATION_TIMEOUT_SECONDS) {
          clearInterval(refinePollRef.current); refinePollRef.current = null;
          try { await client.post(`/ai/landing/job/${data.jobId}/cancel`); } catch {}
          toast.error("La retouche a dépassé le délai. Vos blocs sont conservés, vous pouvez réessayer.");
          setRefining(false); setRefineJobId(null); setRefineJobPhase("");
          return;
        }
        try {
          const { data: j } = await client.get(`/ai/landing/job/${data.jobId}`);
          if (j.phase) setRefineJobPhase(j.phase);
          if (elapsed >= 30 && elapsed < 55) setRefineJobPhase("✨ Presque terminé…");
          else if (elapsed >= 55) setRefineJobPhase(`⏳ En cours (${elapsed}s)…`);
          if (j.status === "done") {
            clearInterval(refinePollRef.current); refinePollRef.current = null;
            setBlocks(j.blocks);
            setPreAIBlocks(j.blocks.flatMap((b) => b.sourceBlocks || [b]));
            setRefineInstruction("");
            const u = j.usage || {};
            toast.success(`✨ Modification appliquée · ~${u.input || 0} tokens in / ${u.output || 0} out · coût $${(u.usd || 0).toFixed(4)}`);
            setRefining(false); setRefineJobId(null); setRefineJobPhase("");
          } else if (j.status === "error" || j.status === "cancelled") {
            clearInterval(refinePollRef.current); refinePollRef.current = null;
            toast.error(j.error || "Échec IA — reformulez");
            setRefining(false); setRefineJobId(null); setRefineJobPhase("");
          }
        } catch { /* transient */ }
      }, 3000);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Impossible de démarrer la modification");
      setRefining(false); setRefineJobPhase("");
    }
  };

  const publishFromReview = async () => {
    await save(true);
    setReviewMode(false);
  };

  const restartAI = () => {
    if (!preAIBlocks?.length) { toast.error("Impossible de recommencer — pas de version antérieure"); return; }
    // On ne touche PAS à preAIBlocks — il doit être conservé tant que l'utilisateur ne dépublie pas explicitement.
    setBlocks(preAIBlocks);
    setReviewMode(false);
    setAiInstruction("Libre arbitre sur le design général");
    setTimeout(() => {
      document.querySelector('[data-testid=landing-ai-run]')?.click();
    }, 100);
  };

  if (!selectedEvent) return (<><PageHeader title="Éditeur de landing page" /><NoEvent /></>);

  const days = daysByEvent(selectedEvent.id);
  const acts = days.flatMap((d) => activitiesByDay(d.id));
  const ctx = { event: selectedEvent, days, activities: acts, registration: reg, company };

  const upd = (id, patch) => setBlocks((bs) => bs.map((b) => b.id === id ? { ...b, ...patch } : b));
  const move = (i, dir) => setBlocks((bs) => { const j = i + dir; if (j < 0 || j >= bs.length) return bs; const c = [...bs]; [c[i], c[j]] = [c[j], c[i]]; return c; });
  const reorder = (from, to) => setBlocks((bs) => { if (from === to || from == null) return bs; const c = [...bs]; const [m] = c.splice(from, 1); c.splice(to, 0, m); return c; });
  const remove = (id) => setBlocks((bs) => bs.filter((b) => b.id !== id));
  const add = (type) => setBlocks((bs) => [...bs, newBlock(type)]);
  const updItem = (bid, idx, patch) => setBlocks((bs) => bs.map((b) => b.id === bid ? { ...b, items: b.items.map((it, i) => i === idx ? { ...it, ...patch } : it) } : b));
  const addItem = (bid) => setBlocks((bs) => bs.map((b) => b.id === bid ? { ...b, items: [...(b.items || []), {}] } : b));
  const delItem = (bid, idx) => setBlocks((bs) => bs.map((b) => b.id === bid ? { ...b, items: b.items.filter((_, i) => i !== idx) } : b));
  const uploadTo = async (file, cb) => { try { const url = await uploadFile(file); cb(url); } catch { toast.error("Échec upload"); } };

  const save = async (pub) => {
    try {
      const { ...res } = (await client.put(`/landing/${selectedEvent.id}`, { blocks, slug, published: pub ?? published, preAIBlocks })).data;
      setSlug(res.slug); if (pub !== undefined) setPublished(pub);
      toast.success(pub === true ? "Landing page publiée 🎉" : pub === false ? "Publication retirée" : "Enregistré");
    } catch { toast.error("Enregistrement impossible, réessayez"); }
  };

  const publicUrl = `${window.location.origin}/p/${slug || "…"}`;

  const editorFor = (b) => (
    <div className="space-y-2">
      {b.type === "ai_page" && (
        <div className="rounded-lg border border-violet-200 bg-violet-50 p-3 text-xs text-violet-900 space-y-2">
          <p className="flex items-center gap-1.5 font-semibold" data-testid="ai-content-preserved"><Sparkles className="h-3.5 w-3.5" />{b.sourceBlocks ? `${b.sourceBlocks.length} blocs conservés · design IA` : "Ancienne page HTML IA"}</p>
          <p className="text-[11px]" data-testid="ai-content-explanation">{b.sourceBlocks ? "Tous vos blocs et leurs données sont conservés. L'IA modifie uniquement la présentation ; le programme et l'inscription restent reliés à l'événement." : "Relancez le design IA depuis vos blocs originaux pour bénéficier de la conservation intégrale du contenu."}</p>
          <div className="flex flex-wrap gap-2 pt-1">
            <Button size="sm" variant="outline" onClick={() => setReviewMode(true)} data-testid="ai-page-review" className="border-violet-300 text-violet-700"><Sparkles className="h-3 w-3 mr-1" />Réviser</Button>
            {preAIBlocks?.length ? <Button size="sm" variant="outline" onClick={restorePreAI} data-testid="ai-page-restore" className="border-slate-300">↩︎ Retirer le design IA (garder les blocs)</Button> : null}
          </div>
        </div>
      )}
      {!["divider", "footer", "text", "ai_page"].includes(b.type) && <Input placeholder="Titre" value={b.title} onChange={(e) => upd(b.id, { title: e.target.value })} />}
      {b.type === "text" && (<><Input placeholder="Titre (optionnel)" value={b.title} onChange={(e) => upd(b.id, { title: e.target.value })} /><RichTextEditor value={b.html} onChange={(html) => upd(b.id, { html })} /></>)}
      {["hero", "registration", "footer"].includes(b.type) && <Textarea rows={2} placeholder={b.type === "footer" ? "Coordonnées / mentions" : "Texte"} value={b.text} onChange={(e) => upd(b.id, { text: e.target.value })} />}
      {b.type === "button" && <div className="grid grid-cols-2 gap-2"><Input placeholder="Libellé du bouton" value={b.label} onChange={(e) => upd(b.id, { label: e.target.value })} /><Input placeholder="Lien (https://)" value={b.url} onChange={(e) => upd(b.id, { url: e.target.value })} /></div>}
      {b.type === "video" && <Input placeholder="URL vidéo (.mp4)" value={b.url} onChange={(e) => upd(b.id, { url: e.target.value })} />}
      {b.type === "youtube" && <Input placeholder="Lien YouTube" value={b.url} onChange={(e) => upd(b.id, { url: e.target.value })} />}
      {b.type === "maps" && <Input placeholder="Adresse ou lieu (vide = lieu de l'événement)" value={b.query} onChange={(e) => upd(b.id, { query: e.target.value })} />}
      {(b.type === "hero" || b.type === "image") && <div><Label className="text-xs mb-1 block">Image</Label><Input type="file" accept="image/*" onChange={(e) => { const f = e.target.files?.[0]; if (f) uploadTo(f, (url) => upd(b.id, { image: url })); }} /></div>}
      {b.type === "image" && <div className="grid grid-cols-2 gap-2"><div className="flex items-center gap-2 text-xs">Largeur %<Input type="number" min="10" max="100" className="w-20 h-8" value={b.width || 100} onChange={(e) => upd(b.id, { width: Number(e.target.value) })} /></div><Input placeholder="Lien au clic (optionnel)" value={b.url} onChange={(e) => upd(b.id, { url: e.target.value })} /></div>}
      {["image", "button"].includes(b.type) && (
        <Select value={b.align || "center"} onValueChange={(v) => upd(b.id, { align: v })}><SelectTrigger className="h-9"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="left">Aligné à gauche</SelectItem><SelectItem value="center">Centré</SelectItem><SelectItem value="right">Aligné à droite</SelectItem></SelectContent></Select>
      )}
      {b.type !== "divider" && (
        <div className="flex items-center gap-4 flex-wrap text-xs">
          <label className="flex items-center gap-2">Fond<input type="color" value={b.bg || "#ffffff"} onChange={(e) => upd(b.id, { bg: e.target.value })} className="h-8 w-12 rounded border border-border" /></label>
          {b.type !== "footer" && <label className="flex items-center gap-2">Accent<input type="color" value={b.accent || selectedEvent.color || "#6366f1"} onChange={(e) => upd(b.id, { accent: e.target.value })} className="h-8 w-12 rounded border border-border" /></label>}
        </div>
      )}
      {HAS_ITEMS[b.type] && (
        <div className="space-y-2 pt-1">
          {(b.items || []).map((it, idx) => (
            <div key={idx} className="flex flex-wrap gap-2 items-center border border-border rounded-lg p-2">
              {HAS_ITEMS[b.type].map((k) => UPLOAD_KEYS.includes(k)
                ? <div key={k} className="flex-1 min-w-[140px]"><Label className="text-[10px] block mb-0.5">{k === "photo" ? "Photo" : k === "logo" ? "Logo" : "Image"}</Label><Input type="file" accept="image/*" className="text-xs" onChange={(e) => { const f = e.target.files?.[0]; if (f) uploadTo(f, (url) => updItem(b.id, idx, { [k]: url })); }} /></div>
                : <Input key={k} className="flex-1 min-w-[140px] text-sm" placeholder={PLACE[k] || k} value={it[k] || ""} onChange={(e) => updItem(b.id, idx, { [k]: e.target.value })} />)}
              <Button size="icon" variant="ghost" className="h-8 w-8 shrink-0" onClick={() => delItem(b.id, idx)}><Trash2 className="h-3.5 w-3.5 text-rose-600" /></Button>
            </div>
          ))}
          <Button size="sm" variant="outline" onClick={() => addItem(b.id)}><Plus className="h-3.5 w-3.5 mr-1" />Ajouter</Button>
        </div>
      )}
      {b.type === "registration" && (
        <p className="text-xs text-indigo-700 bg-indigo-50 border border-indigo-200 rounded p-2" data-testid="reg-block-linked">
          🔗 Ce bloc reflète automatiquement la configuration <b>Branding → Inscription</b> (couleurs, billets, champs, codes promo).
          {!reg && <span className="block mt-1 text-amber-700">⚠️ Configurez d'abord Branding → Inscription pour activer l'aperçu.</span>}
        </p>
      )}
      {b.type === "registration" && !reg && null}
    </div>
  );

  return (
    <>
      <PageHeader title="Éditeur de landing page"
        actions={<div className="flex gap-2 flex-wrap"><EventSelector /><Button variant="outline" onClick={() => save()} data-testid="landing-save"><Save className="h-4 w-4 mr-1" />Enregistrer</Button>{blocks.some((b) => b.type === "ai_page") && !published ? <Button onClick={() => setReviewMode(true)} data-testid="landing-review" className="bg-violet-600 hover:bg-violet-700"><Sparkles className="h-4 w-4 mr-1" />Réviser & publier</Button> : published ? <Button variant="outline" onClick={() => save(false)} data-testid="landing-unpublish">Dépublier</Button> : <Button onClick={() => save(true)} data-testid="landing-publish"><Globe className="h-4 w-4 mr-1" />Publier</Button>}</div>} />

      <Dialog open={reviewMode} onOpenChange={(v) => setReviewMode(v)}>
        <DialogContent className="max-w-6xl w-[95vw] max-h-[90vh] overflow-y-auto" data-testid="review-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Sparkles className="h-5 w-5 text-violet-600" />Révision avant publication</DialogTitle>
            <DialogDescription>Passez en revue votre page IA. Demandez des modifications ciblées à l'IA, publiez telle quelle, ou recommencez depuis vos blocs originaux.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="rounded-xl border border-border overflow-hidden max-h-[50vh] overflow-y-auto shadow-inner bg-white" data-testid="review-preview">
              <LandingView blocks={blocks} ctx={ctx} />
            </div>
            <div className="rounded-lg border border-violet-200 bg-violet-50 p-3 space-y-2">
              <Label className="text-xs font-semibold">💬 Demander une modification à l'IA</Label>
              <Textarea rows={3} value={refineInstruction} onChange={(e) => setRefineInstruction(e.target.value)} disabled={refining} data-testid="refine-instruction" placeholder={`Exemples :\n• « Rends le titre plus grand »\n• « Change la couleur du bouton en vert »\n• « Présente les intervenants en grille »\n• « Ajoute plus d'espace entre les sections »`} className="bg-white text-sm" />
              <div className="flex justify-end items-center gap-2">
                {refining && refineJobPhase && <span className="text-[11px] text-violet-700 font-medium mr-auto" data-testid="refine-phase">{refineJobPhase}</span>}
                <Button onClick={applyRefinement} disabled={refining || !refineInstruction.trim()} data-testid="refine-apply" size="sm" className="bg-violet-600 hover:bg-violet-700">{refining ? <><Sparkles className="h-3.5 w-3.5 mr-1 animate-spin" />Application…</> : <><Sparkles className="h-3.5 w-3.5 mr-1" />Appliquer les modifications</>}</Button>
              </div>
            </div>
            <DialogFooter className="flex-wrap gap-2 justify-between sm:justify-between">
              <div className="flex flex-wrap gap-2">
                <Button variant="outline" onClick={restartAI} data-testid="review-restart" disabled={!preAIBlocks?.length}>↻ Recommencer depuis les blocs</Button>
                <Button variant="outline" onClick={restorePreAI} data-testid="review-restore" disabled={!preAIBlocks?.length}>↩︎ Retirer le design IA</Button>
              </div>
              <div className="flex flex-wrap gap-2">
                <Button variant="outline" onClick={() => setReviewMode(false)} data-testid="review-close">Fermer</Button>
                <Button onClick={publishFromReview} data-testid="review-publish" className="bg-emerald-600 hover:bg-emerald-700"><Globe className="h-4 w-4 mr-1" />Publier telle quelle</Button>
              </div>
            </DialogFooter>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!aiVariants} onOpenChange={(v) => !v && setAiVariants(null)}>
        <DialogContent className="max-w-6xl w-[95vw] max-h-[90vh] overflow-y-auto" data-testid="ai-variants-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Sparkles className="h-5 w-5 text-violet-600" />Choisissez votre variante préférée</DialogTitle>
            <DialogDescription>L'IA a généré {aiVariants?.variants?.length || 0} {(aiVariants?.variants?.length || 0) === 1 ? "version différente" : "versions différentes"}. Cliquez sur une carte pour la prévisualiser, puis appliquez celle qui vous convient. Votre landing actuelle reste inchangée tant que vous n'appliquez pas.{aiVariants?.usage && <span className="block mt-1 text-[11px] font-mono text-violet-700">📊 Consommation IA : ~{aiVariants.usage.input} tokens in · {aiVariants.usage.output} tokens out · coût estimé ${(aiVariants.usage.usd || 0).toFixed(4)} ({aiVariants.usage.variants_count} variantes)</span>}</DialogDescription>
          </DialogHeader>
          {aiVariants && (
            <div className="space-y-4">
              <div className="grid grid-cols-3 gap-2">
                {aiVariants.variants.map((v, i) => (
                  <button key={i} onClick={() => setAiVariants((p) => ({ ...p, activeIdx: i }))} data-testid={`ai-variant-tab-${i}`}
                    className={`p-3 rounded-lg border-2 text-left transition ${aiVariants.activeIdx === i ? "border-violet-600 bg-violet-50" : "border-slate-200 bg-white hover:border-slate-300"}`}>
                    <p className="text-xs font-semibold text-violet-700">Variante {i + 1}</p>
                    <p className="text-sm font-semibold mt-0.5">{v.label}</p>
                    <p className="text-[11px] text-muted-foreground mt-1" data-testid={`ai-variant-block-count-${i}`}>{v.blocks.reduce((total, b) => total + (b.sourceBlocks?.length || 1), 0)} blocs conservés</p>
                  </button>
                ))}
              </div>
              <div className="rounded-xl border border-border overflow-hidden max-h-[55vh] overflow-y-auto shadow-inner bg-white" data-testid="ai-variant-preview">
                <LandingView blocks={aiVariants.variants[aiVariants.activeIdx]?.blocks || []} ctx={ctx} />
              </div>
              <DialogFooter className="flex-wrap gap-2">
                <Button variant="outline" onClick={() => setAiVariants(null)} data-testid="ai-variants-cancel">Fermer sans appliquer</Button>
                <Button onClick={() => applyVariant(aiVariants.activeIdx)} data-testid="ai-variants-apply" className="bg-violet-600 hover:bg-violet-700"><Check className="h-4 w-4 mr-1" />Appliquer la variante {aiVariants.activeIdx + 1}</Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>


      {published && (
        <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 mb-6 flex items-center gap-3 flex-wrap" data-testid="landing-public-bar">
          <Globe className="h-4 w-4 text-emerald-600 shrink-0" />
          <span className="text-sm font-semibold text-emerald-800 shrink-0">Page en ligne :</span>
          <a href={publicUrl} target="_blank" rel="noreferrer" className="flex-1 min-w-[180px] text-sm text-emerald-700 underline truncate" data-testid="landing-public-url">{publicUrl}</a>
          <Button size="sm" variant="outline" onClick={() => { navigator.clipboard?.writeText(publicUrl); toast.success("Lien copié"); }} data-testid="landing-copy"><Copy className="h-4 w-4 mr-1" />Copier</Button>
          <Button size="sm" variant="outline" onClick={() => window.open(publicUrl, "_blank")}><ExternalLink className="h-4 w-4 mr-1" />Ouvrir</Button>
          <Button size="sm" variant="outline" onClick={() => { const iframe = `<iframe src="${publicUrl}?embed=1" width="100%" height="1200" frameborder="0" style="border:0;background:transparent" allow="payment"></iframe>`; navigator.clipboard?.writeText(iframe); toast.success("Code d'intégration iFrame copié — collez-le dans votre site"); }} data-testid="landing-copy-iframe"><Code className="h-4 w-4 mr-1" />Copier iFrame</Button>
        </div>
      )}

      {!loaded ? null : (
      <div className="grid lg:grid-cols-2 gap-6">
        <div className="space-y-4">
          <div className="bg-card rounded-xl border border-border p-4">
            <Label className="mb-1.5 block">Slug de l'URL publique</Label>
            <div className="flex items-center gap-2"><span className="text-sm text-muted-foreground">/p/</span><Input value={slug} onChange={(e) => setSlug(e.target.value)} placeholder="mon-evenement-2026" data-testid="landing-slug" /></div>
          </div>

          <div className="flex flex-wrap gap-2 bg-card rounded-xl border border-border p-4">
            <span className="text-xs font-semibold text-muted-foreground w-full mb-1">Ajouter un bloc :</span>
            {PALETTE.map((t) => <Button key={t} size="sm" variant="outline" onClick={() => add(t)} data-testid={`add-block-${t}`}><Plus className="h-3.5 w-3.5 mr-1" />{BLOCK_META[t]}</Button>)}
          </div>

          {blocks.map((b, i) => (
            <div key={b.id} draggable onDragStart={() => { dragI.current = i; }} onDragOver={(e) => e.preventDefault()} onDrop={() => { reorder(dragI.current, i); dragI.current = null; }}
              className="bg-card rounded-xl border border-border p-4" data-testid={`block-editor-${b.type}`}>
              <div className="flex items-center justify-between mb-3">
                <span className="font-display font-semibold text-sm flex items-center gap-2"><GripVertical className="h-4 w-4 text-slate-300 cursor-grab" />{BLOCK_META[b.type]}</span>
                <div className="flex gap-1">
                  <Button size="icon" variant="ghost" className="h-7 w-7" disabled={i === 0} onClick={() => move(i, -1)} data-testid={`block-up-${b.id}`}><ArrowUp className="h-4 w-4" /></Button>
                  <Button size="icon" variant="ghost" className="h-7 w-7" disabled={i === blocks.length - 1} onClick={() => move(i, 1)} data-testid={`block-down-${b.id}`}><ArrowDown className="h-4 w-4" /></Button>
                  <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => remove(b.id)} data-testid={`block-del-${b.id}`}><Trash2 className="h-4 w-4 text-rose-600" /></Button>
                </div>
              </div>
              {editorFor(b)}
            </div>
          ))}

          <div className="bg-gradient-to-br from-violet-50 to-indigo-50 rounded-xl border border-violet-200 p-4 space-y-3" data-testid="landing-ai-bar">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <p className="text-sm font-display font-semibold flex items-center gap-2"><Sparkles className="h-4 w-4 text-violet-600" />Demandez à l'IA de retravailler votre landing</p>
              <span className="text-[11px] text-violet-700">✨ Mode démo — générations illimitées</span>
            </div>
            <p className="text-[11px] text-slate-600" data-testid="landing-ai-content-rule"><b>Tous vos blocs et leurs données sont conservés à l'identique</b>, dans leur ordre : programme, textes, intervenants, compte à rebours… L'IA ne crée que le design. Pour modifier le contenu, éditez les blocs.</p>
            <Textarea rows={3} value={aiInstruction} onChange={(e) => setAiInstruction(e.target.value)} disabled={aiLoading} data-testid="landing-ai-instruction" placeholder={`Exemples :\n• « Présente la galerie en mosaïque »\n• « Mets en valeur les logos des sponsors »\n• « Libre arbitre sur le design général »`} className="bg-white" />
            <div className="flex justify-end gap-2 items-center">
              {aiLoading && aiJobPhase && <span className="text-xs text-violet-700 font-medium mr-auto" data-testid="landing-ai-phase">{aiJobPhase}</span>}
              {aiLoading ? (
                <>
                  <Button variant="outline" size="sm" onClick={cancelAI} data-testid="landing-ai-cancel">Annuler</Button>
                  <Button disabled data-testid="landing-ai-run" className="bg-violet-600 hover:bg-violet-700"><Sparkles className="h-4 w-4 mr-1 animate-spin" />L'IA travaille… (30 s à 1 min)</Button>
                </>
              ) : (
                <>
                  <Button variant="outline" size="sm" onClick={() => setAiInstruction("Libre arbitre sur le design général")} data-testid="landing-ai-libre">💡 Libre arbitre</Button>
                  <Button onClick={generateAI} data-testid="landing-ai-run" className="bg-violet-600 hover:bg-violet-700"><Sparkles className="h-4 w-4 mr-1" />Appliquer avec l'IA</Button>
                </>
              )}
            </div>
          </div>
        </div>

        <div className="lg:sticky lg:top-4 self-start">
          <p className="text-sm font-semibold mb-2 flex items-center gap-2"><Sparkles className="h-4 w-4 text-indigo-600" />Aperçu en direct</p>
          <div className="rounded-xl border border-border overflow-hidden max-h-[80vh] overflow-y-auto shadow-sm" data-testid="landing-preview">
            <LandingView blocks={blocks} ctx={ctx} />
          </div>
        </div>
      </div>
      )}
    </>
  );
}
